import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import baseURL from '../../../baseURL';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Snackbar,
  Alert,
  Chip
} from '@mui/material';
import { Fingerprint as FingerprintIcon } from '@mui/icons-material';
import GirviPageShell from '../../common/GirviPageShell';
import { receiptService } from '../../../services/receiptService';
import { resolvePledgeDhara, calendarDaysBetween } from '../../../utils/dharaInterest';
import { interestForSession, isBlackLogin } from '../../../utils/blackInterest';
import { DEFAULT_DHARA } from '../../../constants/dhara';

/** VPS A — MONTHS and AS ON DATE interest receipts. */
const INTEREST_TYPE_OPTIONS = [
  { value: 'MONTHS', label: 'MONTHS' },
  { value: 'AS_ON_DATE', label: 'AS ON DATE' }
];

const CreateReceipt = () => {
  const [date, setDate] = useState(new Date());
  const [transactionType, setTransactionType] = useState('CPT');
  const [ledgerName, setLedgerName] = useState('');
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [filteredLedgers, setFilteredLedgers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLedgerDetails, setShowLedgerDetails] = useState(false);
  const [selectedLedgerForDetails, setSelectedLedgerForDetails] = useState(null);
  const [pledges, setPledges] = useState([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [selectedInterestType, setSelectedInterestType] = useState('MONTHS');
  const [months, setMonths] = useState('');
  const [calculatedInterest, setCalculatedInterest] = useState(0);
  const [standardInterest, setStandardInterest] = useState(0);
  const [standardInterestAmount, setStandardInterestAmount] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [adjustmentType, setAdjustmentType] = useState('PRINCIPAL');
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [pledgeDetails, setPledgeDetails] = useState(null);
  const [loadingPledge, setLoadingPledge] = useState(false);
  const [showInterestTypeModal, setShowInterestTypeModal] = useState(false);
  const [adjustedPrincipal, setAdjustedPrincipal] = useState('');
  const [adjustedInterest, setAdjustedInterest] = useState('');
  const adjustedInterestRef = useRef(null);
  const [ledgerPledges, setLedgerPledges] = useState([]);
  const [loadingLedgerPledges, setLoadingLedgerPledges] = useState(false);
  const [showLedgerPledges, setShowLedgerPledges] = useState(false);
  const [pledgeReceipts, setPledgeReceipts] = useState([]);
  const [loadingPledgeReceipts, setLoadingPledgeReceipts] = useState(false);
  const [showPledgeReceipts, setShowPledgeReceipts] = useState(false);
  // Fingerprint scanning states
  const [fingerprintScanning, setFingerprintScanning] = useState(false);
  const [fingerprintMatches, setFingerprintMatches] = useState([]);
  const [showFingerprintMatches, setShowFingerprintMatches] = useState(false);
  const [fingerprintMessage, setFingerprintMessage] = useState('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editReceiptId = searchParams.get('edit');
  const [isEditMode, setIsEditMode] = useState(false);

  // Ensure body scroll is restored when entering Receipt (avoids stuck state from other screens)
  useEffect(() => {
    document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const ledgerSearchTimeout = useRef(null);

  // Search ledgers via API (code, full name, mobile)
  useEffect(() => {
    if (!ledgerName || selectedLedger) {
      setFilteredLedgers([]);
      return;
    }

    const term = ledgerName.trim();
    if (!term) {
      setFilteredLedgers([]);
      return;
    }

    clearTimeout(ledgerSearchTimeout.current);
    ledgerSearchTimeout.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${baseURL}/ledgers?type=Customer&search=${encodeURIComponent(term)}&limit=50`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) {
          setFilteredLedgers([]);
          return;
        }

        const data = await response.json();
        const ledgersData = Array.isArray(data) ? data : (data.data || data.ledgers || []);
        setFilteredLedgers(Array.isArray(ledgersData) ? ledgersData : []);
      } catch (error) {
        setFilteredLedgers([]);
      }
    }, 300);

    return () => clearTimeout(ledgerSearchTimeout.current);
  }, [ledgerName, selectedLedger]);

  const fetchLedgerByCode = async (code) => {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${baseURL}/ledgers?type=Customer&search=${encodeURIComponent(code)}&limit=5`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) return null;
    const data = await response.json();
    const ledgersData = Array.isArray(data) ? data : (data.data || data.ledgers || []);
    return (Array.isArray(ledgersData) ? ledgersData : []).find((l) => l.code === code) || null;
  };
  useEffect(() => {
    const fetchReceiptForEdit = async () => {
      if (editReceiptId) {
        try {
          setIsEditMode(true);
          const token = localStorage.getItem('token');
          const response = await fetch(`${baseURL}/receipts/${editReceiptId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            const receipt = data.data || data.receipt || data;
            
            if (receipt) {
              // Set date
              if (receipt.date) {
                const receiptDate = new Date(receipt.date);
                setDate(receiptDate);
              }
              
              // Set ledger
              if (receipt.ledger) {
                setSelectedLedger(receipt.ledger);
                const ledgerNameStr = `${receipt.ledger.firstName || ''} ${receipt.ledger.lastName || ''}`.trim();
                setLedgerName(ledgerNameStr);
              }
              
              // Set ticket/pledge
              if (receipt.pledge) {
                const pledge = receipt.pledge;
                const ticketNo = pledge.ticketNo || pledge.loanDetails?.ticketNo;
                const principalAmount = pledge.loanDetails?.principalAmount || 0;
                
                // Calculate pending principal (principal - total principal paid)
                const totalPrincipalPaid = pledge.totalPrincipalPaid || 0;
                const pendingPrincipal = Math.max(0, principalAmount - totalPrincipalPaid);
                
                const interestAmount = receipt.interestDetails?.calculatedInterest ?? 0;
                const pledgeForForm = {
                  ticketNo: ticketNo,
                  principal: principalAmount,
                  pendingPrincipal: pendingPrincipal,
                  adjustedPrincipal: receipt.adjustments?.principalAdjustment || 0,
                  standardInterest: interestAmount,
                  adjustedInterest: receipt.adjustments?.interestAdjustment || 0,
                  _originalPledge: pledge
                };
                
                setSelectedTicket(pledge);
                setPledges([pledgeForForm]);
              }
              
              // Set transaction type
              if (receipt.transactionType) {
                setTransactionType(receipt.transactionType);
              }
              
              // Set amounts
              if (receipt.totalAmount) {
                setReceivedAmount(receipt.totalAmount);
              }
              
              // Set interest details if available
              if (receipt.interestDetails) {
                const calculatedInt = receipt.interestDetails.calculatedInterest || 0;
                setCalculatedInterest(calculatedInt);
                setTotalInterest(calculatedInt);
                setStandardInterestAmount(calculatedInt);
                
                // Set interest type and months
                if (receipt.interestDetails.type) {
                  setSelectedInterestType(receipt.interestDetails.type);
                }
                if (receipt.interestDetails.months) {
                  setMonths(receipt.interestDetails.months.toString());
                }
                
              }
              
              // Set adjustments if available
              if (receipt.adjustments) {
                if (receipt.adjustments.principalAdjustment) {
                  setAdjustedPrincipal(receipt.adjustments.principalAdjustment.toString());
                }
                if (receipt.adjustments.interestAdjustment) {
                  setAdjustedInterest(receipt.adjustments.interestAdjustment.toString());
                }
                if (receipt.adjustments.adjustToPrincipal !== undefined) {
                  setAdjustmentType(receipt.adjustments.adjustToPrincipal ? 'PRINCIPAL' : 'INTEREST');
                }
              }
            } else {
            }
          } else {
            const errorData = await response.json();
          }
        } catch (error) {
        }
      }
    };

    fetchReceiptForEdit();
  }, [editReceiptId]);


  // Load receipt data for edit mode
  useEffect(() => {
    
    if (selectedTicket && standardInterestAmount > 0) {
      // Use the correct ticket number path
      const ticketNumber = selectedTicket.ticketNo || selectedTicket.loanDetails?.ticketNo;
      
      const pledgeIndex = pledges.findIndex(p => {
        return p.ticketNo === ticketNumber;
      });
      
      if (pledgeIndex !== -1) {
        const newPledges = [...pledges];
        const updatedPledge = {
          ...newPledges[pledgeIndex],
          calculatedInterest: totalInterest,
          standardInterest: standardInterestAmount
        };
        newPledges[pledgeIndex] = updatedPledge;
        setPledges(newPledges);
      } else {
      }
    } else {
    }
  }, [totalInterest, standardInterestAmount, selectedTicket]);

  const fetchPledgeDetails = async (ticketNo) => {
    try {
      if (!selectedLedger) {
        throw new Error('Please select a ledger first');
      }

      setLoadingPledge(true);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/ticket/${ticketNo}?clientCode=${selectedLedger.code}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch pledge');
      }

      const data = await response.json();

      if (!data || !data.pledge) {
        throw new Error('Invalid pledge data received');
      }

      setSelectedTicket({
        ...data.pledge,
        secondaryTransaction: data.secondaryTransaction || data.pledge.secondaryTransaction || null,
        loanDetails: {
          ...data.pledge.loanDetails,
          ticketNo: data.pledge.loanDetails.ticketNo,
          principalAmount: data.pledge.loanDetails.principalAmount,
          articles: data.pledge.loanDetails.articles
        },
        customer: data.pledge.customer,
        articles: data.pledge.articles,
        date: data.pledge.date,
        fingerprint: data.pledge.fingerprint,
        clientPhoto: data.pledge.clientPhoto,
        articlePhoto: data.pledge.articlePhoto
      });

      // Store basic details for table
      setPledgeDetails({
        ticketNo: data.pledge.loanDetails.ticketNo,
        principalAmount: data.pledge.loanDetails.principalAmount,
        createdAt: data.pledge.date
      });

      // Fetch receipts for this pledge
      fetchPledgeReceipts(data.pledge.loanDetails.ticketNo);

      // Calculate interest immediately if we have months selected
      if (months) {
        calculateInterest();
      }
    } catch (error) {
      setPledgeDetails(null);
      setShowSnackbar(true);
      setSnackbarMessage(error.message);
      throw error;
    } finally {
      setLoadingPledge(false);
    }
  };

  const handleLedgerClick = (ledger) => {
    setSelectedLedgerForDetails(ledger);
    setShowLedgerDetails(true);
  };

  const handleLedgerDetailsConfirm = () => {
    setSelectedLedger(selectedLedgerForDetails);
    setLedgerName(selectedLedgerForDetails.name);
    setShowLedgerDetails(false);
    // Fetch pledges for the selected ledger
    fetchLedgerPledges(selectedLedgerForDetails.code);
  };

  const handleLedgerDetailsClose = () => {
    setShowLedgerDetails(false);
  };

  const fetchLedgerPledges = async (ledgerCode) => {
    try {
      setLoadingLedgerPledges(true);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/client/${ledgerCode}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pledges');
      }

      const data = await response.json();
      
      if (data.success && data.pledges) {
        // Filter to only show CREATED pledges (not RELEASED)
        const createdPledges = data.pledges.filter(pledge => 
          pledge.status === 'CREATED' || (!pledge.status && !pledge.loanDetails?.released)
        );
        setLedgerPledges(createdPledges);
        setShowLedgerPledges(true);
      } else {
        setLedgerPledges([]);
        setShowSnackbar(true);
        setSnackbarMessage('No pledges found for this ledger');
      }
    } catch (error) {
      setLedgerPledges([]);
      setShowSnackbar(true);
      setSnackbarMessage('Error fetching pledges');
    } finally {
      setLoadingLedgerPledges(false);
    }
  };

  // Helper function to format date as dd/mm/yyyy
  // Fetch receipts for a pledge
  const fetchPledgeReceipts = async (ticketNo) => {
    try {
      setLoadingPledgeReceipts(true);
      setShowPledgeReceipts(true);
      const token = localStorage.getItem('token');
      const url = `${baseURL}/receipts?ticketNo=${ticketNo}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Failed to fetch receipts');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        setPledgeReceipts(data.data);
      } else {
        setPledgeReceipts([]);
      }
    } catch (error) {
      setPledgeReceipts([]);
      setShowSnackbar(true);
      setSnackbarMessage(`Error fetching receipts: ${error.message}`);
    } finally {
      setLoadingPledgeReceipts(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Handle fingerprint scanning — auto-select ledger and show ALL active pledges
  const handleFingerprintScan = async () => {
    try {
      setFingerprintScanning(true);
      setFingerprintMessage('Scanning fingerprint... Please place your finger on the scanner.');
      setFingerprintMatches([]);
      
      const result = await receiptService.searchPledgeByFingerprint(60);
      
      if (result.matches && result.matches.length > 0) {
        const activeMatches = result.matches.filter(
          pledge => pledge.status !== 'RELEASED' && pledge.loanDetails?.released !== true
        );
        
        const sortedMatches = activeMatches.sort(
          (a, b) => (b.fingerprintMatchScore || 0) - (a.fingerprintMatchScore || 0)
        );
        
        if (sortedMatches.length > 0) {
          const firstMatch = sortedMatches[0];
          const customerCode = firstMatch.customer?.code;

          if (customerCode) {
            const matchedLedger = await fetchLedgerByCode(customerCode);
            if (matchedLedger) {
              setSelectedLedger(matchedLedger);
              setLedgerName(`${matchedLedger.firstName || ''} ${matchedLedger.lastName || ''}`.trim());
              fetchLedgerPledges(matchedLedger.code);
              setFingerprintMessage(
                `Ledger auto-selected: ${matchedLedger.firstName || ''} ${matchedLedger.lastName || ''}. Showing all active pledges.`
              );
              setShowSnackbar(true);
              setSnackbarMessage(`Ledger found. Showing all active pledges for ${matchedLedger.firstName || ''}`);
            } else {
              const customer = firstMatch.customer;
              const ledgerData = {
                _id: customer._id || firstMatch._id,
                code: customer.code,
                firstName: customer.firstName,
                lastName: customer.lastName,
                occupation: customer.occupation,
                address: customer.address,
                mobileNo: customer.mobileNo
              };
              setSelectedLedger(ledgerData);
              setLedgerName(`${customer.firstName || ''} ${customer.lastName || ''}`.trim());
              fetchLedgerPledges(customer.code);
              setFingerprintMessage(
                `Customer: ${customer.firstName || ''} ${customer.lastName || ''}. Showing all active pledges.`
              );
              setShowSnackbar(true);
              setSnackbarMessage(`Customer found. Showing all active pledges.`);
            }
          } else {
            setFingerprintMatches(sortedMatches);
            setShowFingerprintMatches(true);
            setFingerprintMessage(
              `Found ${sortedMatches.length} active pledge(s). Quality: ${result.scanned?.quality || 0}%`
            );
          }
        } else {
          setFingerprintMessage('No active pledges found. All matches are released.');
          setShowSnackbar(true);
          setSnackbarMessage('No active pledges found');
        }
      } else {
        setFingerprintMessage('No matching pledges found. Please try manual search.');
        setShowSnackbar(true);
        setSnackbarMessage('No matching pledges found');
      }
    } catch (error) {
      setFingerprintMessage(error.message || 'Failed to scan fingerprint');
      setShowSnackbar(true);
      setSnackbarMessage(error.message || 'Failed to scan fingerprint');
    } finally {
      setFingerprintScanning(false);
    }
  };

  // Handle selecting a pledge from fingerprint matches
  const handleFingerprintPledgeSelect = (pledge) => {
    
    // First, set the customer/ledger from the pledge
    const customer = pledge.customer;
    if (customer) {
      const ledgerData = {
        _id: customer._id || pledge._id,
        code: customer.code,
        firstName: customer.firstName,
        lastName: customer.lastName,
        occupation: customer.occupation,
        address: customer.address,
        mobileNo: customer.mobileNo
      };
      
      setSelectedLedger(ledgerData);
      setLedgerName(`${customer.firstName} ${customer.lastName || ''}`.trim());
    }
    
    // Check if pledge already exists in the table
    const existingPledge = pledges.find(p => p.ticketNo === pledge.loanDetails.ticketNo);
    if (existingPledge) {
      setSnackbarMessage('Pledge already added to table');
      setShowSnackbar(true);
      setShowFingerprintMatches(false);
      return;
    }
    
    // Set the complete selectedTicket
    setSelectedTicket({
      ticketNo: pledge.loanDetails.ticketNo,
      secondaryTransaction: pledge.secondaryTransaction || pledge.illegalTransaction || null,
      loanDetails: {
        ticketNo: pledge.loanDetails.ticketNo,
        principalAmount: pledge.loanDetails.principalAmount,
        rateOfInterest: pledge.loanDetails.rateOfInterest || 0,
        articles: pledge.articles || []
      },
      customer: pledge.customer,
      articles: pledge.articles || [],
      date: pledge.date,
    });
    
    // Set pledge details for the table
    setPledgeDetails({
      ticketNo: pledge.loanDetails.ticketNo,
      principalAmount: pledge.loanDetails.principalAmount,
      createdAt: pledge.date
    });
    
    // Fetch receipts for this pledge
    fetchPledgeReceipts(pledge.loanDetails.ticketNo);
    
    // Add pledge to table
    const newPledge = {
      ticketNo: pledge.loanDetails.ticketNo,
      principal: pledge.loanDetails.principalAmount,
      pendingPrincipal: pledge.loanDetails.principalAmount,
      adjustedPrincipal: 0,
      pendingInterest: 0,
      adjustedInterest: 0,
      calculatedInterest: 0,
      standardInterest: 0,
      rateOfInterest: pledge.loanDetails.rateOfInterest || 0,
      secondaryTransaction: pledge.secondaryTransaction || pledge.illegalTransaction || null,
      dhara: pledge.loanDetails.dhara,
      createdAt: pledge.date
    };
    setPledges([...pledges, newPledge]);
    
    // Close fingerprint matches modal
    setShowFingerprintMatches(false);
    setFingerprintMatches([]);
    
    // Show interest type selection modal
    setShowInterestTypeModal(true);
  };

  // Legal login uses the legal rate. Black login uses the illegal rate (F-key % or F10 monthly rupees).
  const calculateInterest = () => {
    if (!selectedTicket) return;

    const duration = selectedInterestType === 'MONTHS' ? parseInt(months, 10) : 1;
    const dhara = resolvePledgeDhara(selectedTicket, DEFAULT_DHARA);
    const actualDays = duration * 30;
    const interest = interestForSession(selectedTicket, {
      actualDays,
      dhara,
      black: isBlackLogin()
    });

    setStandardInterestAmount(interest);
    setTotalInterest(interest);
  };

  const handleTicketSelect = () => {

    if (!selectedTicket?.ticketNo) {
      setSnackbarMessage('Please enter ticket number');
      setShowSnackbar(true);
      return;
    }

    if (!pledgeDetails) {
      setSnackbarMessage('Please fetch pledge details first');
      setShowSnackbar(true);
      return;
    }

    const existingPledge = pledges.find(p => p.ticketNo === selectedTicket.ticketNo);
    if (existingPledge) {
      setSnackbarMessage('Ticket already added');
      setShowSnackbar(true);
      return;
    }

    const newPledge = {
      ticketNo: selectedTicket.ticketNo,
      principal: pledgeDetails.principalAmount,
      pendingPrincipal: pledgeDetails.principalAmount,
      adjustedPrincipal: 0,
      pendingInterest: 0,
      adjustedInterest: 0,
      standardInterest: 0,
      rateOfInterest: selectedTicket?.loanDetails?.rateOfInterest || 0,
      dhara: selectedTicket?.loanDetails?.dhara,
      createdAt: pledgeDetails.createdAt
    };

    setPledges([...pledges, newPledge]);
    
    // Keep selected ticket for interest calculation
    // Only clear it after interest calculation is complete
    setShowTicketModal(false);
    setShowInterestTypeModal(true);
  };

  const handleInterestTypeSelect = (type) => {
    if (type === 'AMOUNT') return;
    setSelectedInterestType(type);
    setShowInterestTypeModal(false);
    
    // If we have a ticket number but no complete pledge details, fetch them first
    if (selectedTicket?.ticketNo && selectedLedger) {
      fetchPledgeDetails(selectedTicket.ticketNo).then(() => {
        // After fetching details, proceed with interest calculation
        if (type === 'MONTHS') {
          setMonths('');
          setShowInterestModal(true);
        } else {
          setMonths('1');
          calculateInterest();
        }
      }).catch(error => {
        setSnackbarMessage('Error fetching pledge details');
        setShowSnackbar(true);
      });
    } else {
      // If we already have complete data, proceed normally
      if (type === 'MONTHS') {
        setMonths('');
        setShowInterestModal(true);
      } else {
        setMonths('1');
        calculateInterest();
      }
    }
  };

  const handleMonthInput = (e) => {
    const value = e.target.value;
    if (value === '' || (value >= 0 && value <= 12)) {
      setMonths(value);
    }
  };

  const handleMonthConfirm = () => {

    if (!months) {
      setSnackbarMessage('Please enter number of months');
      setShowSnackbar(true);
      return;
    }

    if (!selectedTicket?.loanDetails?.principalAmount) {
      setSnackbarMessage('Please select a pledge first');
      setShowSnackbar(true);
      return;
    }

    setSelectedTicket(prev => ({
      ...prev,
      months: months
    }));
    
    calculateInterest();
    setShowInterestModal(false);
    
    // Check if pledge is already in table
    const pledgeExists = pledges.find(p => p.ticketNo === selectedTicket.ticketNo);
    if (selectedTicket?.ticketNo && !pledgeExists) {
      const newPledge = {
        ticketNo: selectedTicket.ticketNo,
        principal: selectedTicket.loanDetails.principalAmount,
        pendingPrincipal: selectedTicket.loanDetails.principalAmount,
        adjustedPrincipal: 0,
        pendingInterest: 0,
        adjustedInterest: 0,
        calculatedInterest: 0,
        standardInterest: 0,
        rateOfInterest: selectedTicket.loanDetails?.rateOfInterest || 0,
        dhara: selectedTicket.loanDetails?.dhara,
        createdAt: selectedTicket.date || new Date()
      };
      setPledges([...pledges, newPledge]);
    } else {
    }
    
    // Clear only the pledge details, keep the ticket number
    setPledgeDetails(null);
  };

  const handleInterestAdjustment = () => {
    const index = pledges.findIndex(p => p.ticketNo === selectedTicket.ticketNo);
    if (index === -1) return;

    const newPledges = [...pledges];
    const pledge = newPledges[index];

    if (adjustmentType === 'PRINCIPAL') {
      // Deduct from principal
      const newAdjustedPrincipal = parseFloat(pledge.adjustedPrincipal) - adjustmentAmount;
      const newPendingInterest = parseFloat(pledge.pendingInterest) + adjustmentAmount;
      
      newPledges[index] = {
        ...pledge,
        adjustedPrincipal: newAdjustedPrincipal,
        pendingInterest: newPendingInterest
      };
    } else {
      // Deduct from interest
      const newPendingInterest = parseFloat(pledge.pendingInterest) - adjustmentAmount;
      newPledges[index] = {
        ...pledge,
        pendingInterest: newPendingInterest
      };
    }

    setPledges(newPledges);
    setReceivedAmount(prev => prev + adjustmentAmount);
    setShowInterestModal(false);
  };

  const handleAdjustedPrincipalChange = (e) => {
    const value = e.target.value;
    setAdjustedPrincipal(value);
    
    if (value === '0' && selectedTicket?.loanDetails?.principalAmount) {
      const principal = selectedTicket.loanDetails.principalAmount;
      const interest = totalInterest;
      const ratio = principal / (principal + interest);
      setStandardInterestAmount(standardInterestAmount * ratio);
      setTotalInterest(standardInterestAmount * ratio);
    }
  };


  const handlePledgeSelect = (pledge) => {
    
    // Check if pledge already exists in the table
    const existingPledge = pledges.find(p => p.ticketNo === pledge.loanDetails.ticketNo);
    if (existingPledge) {
      setSnackbarMessage('Pledge already added to table');
      setShowSnackbar(true);
      return;
    }

    // Use the complete pledge data directly instead of fetching again
    
    // Set the complete selectedTicket with all data
    setSelectedTicket({
      ticketNo: pledge.loanDetails.ticketNo,
      secondaryTransaction: pledge.secondaryTransaction || pledge.illegalTransaction || null,
      loanDetails: {
        ticketNo: pledge.loanDetails.ticketNo,
        principalAmount: pledge.loanDetails.principalAmount,
        rateOfInterest: pledge.loanDetails.rateOfInterest || 0,
        articles: pledge.articles || []
      },
      customer: pledge.customer,
      articles: pledge.articles || [],
      date: pledge.date,
    });

    // Set pledge details for the table
    setPledgeDetails({
      ticketNo: pledge.loanDetails.ticketNo,
      principalAmount: pledge.loanDetails.principalAmount,
      createdAt: pledge.date
    });

    // Fetch receipts for this pledge
    fetchPledgeReceipts(pledge.loanDetails.ticketNo);

    // Add pledge to table immediately (same as manual entry)
    const newPledge = {
      ticketNo: pledge.loanDetails.ticketNo,
      principal: pledge.loanDetails.principalAmount,
      pendingPrincipal: pledge.loanDetails.principalAmount,
      adjustedPrincipal: 0,
      pendingInterest: 0,
      adjustedInterest: 0,
      calculatedInterest: 0,
      standardInterest: 0,
      rateOfInterest: pledge.loanDetails.rateOfInterest || 0,
      secondaryTransaction: pledge.secondaryTransaction || pledge.illegalTransaction || null,
      dhara: pledge.loanDetails.dhara,
      createdAt: pledge.date
    };
    setPledges([...pledges, newPledge]);

    // Close the ledger pledges modal
    setShowLedgerPledges(false);

    // Show interest type selection modal (same flow as manual entry)
    setShowInterestTypeModal(true);
  };


  const applyInterestChoice = (mode, monthValue) => {
    const monthsVal = mode === 'MONTHS' ? String(monthValue ?? '') : months;
    if (mode === 'MONTHS') setMonths(monthsVal);
    setSelectedInterestType(mode);
    setPledges((prev) => {
      const next = prev.map((row) => {
        const rate = Number(row.rateOfInterest) || Number(selectedTicket?.loanDetails?.rateOfInterest) || 0;
        const principal = Number(row.principal) || Number(selectedTicket?.loanDetails?.principalAmount) || 0;
        const dhara = row.dhara || resolvePledgeDhara(selectedTicket, DEFAULT_DHARA);
        const start = row.createdAt || row.date || selectedTicket?.date;
        const actualDays = mode === 'AS_ON_DATE'
          ? calendarDaysBetween(start, date)
          : (parseInt(monthsVal, 10) || 0) * 30;
        const sameTicket = selectedTicket && String(selectedTicket.ticketNo) === String(row.ticketNo);
        const pledgeForRate = {
          ...selectedTicket,
          secondaryTransaction: row.secondaryTransaction || (sameTicket ? selectedTicket?.secondaryTransaction : null) || null,
          loanDetails: {
            ...(selectedTicket?.loanDetails || {}),
            principalAmount: principal,
            rateOfInterest: rate
          },
          principal
        };
        const interest = principal && actualDays
          ? interestForSession(pledgeForRate, { actualDays, dhara, black: isBlackLogin() })
          : 0;
        return {
          ...row,
          rateOfInterest: rate || row.rateOfInterest,
          dhara,
          interestMode: mode,
          months: mode === 'MONTHS' ? monthsVal : row.months,
          standardInterest: interest,
          calculatedInterest: interest,
        };
      });
      const shown = next.reduce((sum, row) => sum + (Number(row.standardInterest) || 0), 0);
      setStandardInterestAmount(shown);
      setTotalInterest(shown);
      return next;
    });
  };

  const handleSaveReceipt = async () => {
    if (!selectedLedger) {
      setSnackbarMessage('Please select a ledger');
      setShowSnackbar(true);
      return;
    }

    if (pledges.length === 0) {
      setSnackbarMessage('Please add at least one pledge to the receipt');
      setShowSnackbar(true);
      return;
    }

    // Use the first pledge in the table for receipt creation
    const firstPledge = pledges[0];
    const pledgeInTable = firstPledge;
    
    const receiptData = {
      date: date.toISOString().split('T')[0],
      transactionType: transactionType,
      ledgerId: selectedLedger._id,
      ticketNo: firstPledge.ticketNo,
      interestType: selectedInterestType,
      months: selectedInterestType === 'MONTHS' ? months : undefined,
      adjustToPrincipal: parseFloat(pledgeInTable?.adjustedPrincipal || 0) > 0,
      adjustToInterest: parseFloat(pledgeInTable?.adjustedPrincipal || 0) === 0
    };


    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/receipts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(receiptData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create receipt');
      }

      setSnackbarMessage(`Receipt created successfully! Receipt No: ${data.receiptNo}`);
      setShowSnackbar(true);
      // Reset form state
      setSelectedLedger(null);
      setSelectedTicket(null);
      setMonths('');
      setTransactionType('CPT');
      setAdjustmentType('PRINCIPAL');
      setPledges([]);
    } catch (error) {
      setSnackbarMessage(error.message || 'Failed to create receipt');
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <GirviPageShell
        title="Create Receipt"
        subtitle="Pledge · Receipt"
        chipLabel="Pledge"
        maxWidth="xl"
        compact
      >
      <Paper elevation={0} sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '148px 120px minmax(220px, 1fr) auto auto' },
            gap: 1.25,
            alignItems: 'center',
            mb: 1.5,
          }}
        >
            <TextField
              type="date"
              label="Date"
              size="small"
              value={date.toISOString().split('T')[0]}
              onChange={(e) => setDate(new Date(e.target.value))}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl size="small">
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
              >
                <MenuItem value="CPT">CPT</MenuItem>
                <MenuItem value="CBT">CBT</MenuItem>
              </Select>
            </FormControl>
            <TextField
                value={selectedLedger ? `${selectedLedger.firstName || ''} ${selectedLedger.lastName || ''}`.trim() : ledgerName || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setLedgerName(value);
                  setSelectedLedger(null);
                  setFilteredLedgers([]);
                }}
                label="Ledger"
                size="small"
                variant="outlined"
                disabled={!!selectedLedger}
                placeholder="Name or code"
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            <Button
              variant="contained"
              color="primary"
              startIcon={<FingerprintIcon />}
              onClick={handleFingerprintScan}
              disabled={fingerprintScanning}
              sx={{ height: 34, whiteSpace: 'nowrap' }}
            >
              {fingerprintScanning ? 'Scanning...' : 'Scan Finger'}
            </Button>
            <Box className="girvi-amount">
              Received ₹{Math.round(receivedAmount)}
            </Box>
        </Box>
          {fingerprintMessage && (
            <Box sx={{ mb: 1.5 }}>
              <Alert severity={fingerprintMatches.length > 0 ? 'success' : 'info'}>
                {fingerprintMessage}
              </Alert>
            </Box>
          )}

                 {/* Ledger Search Results */}
         {ledgerName && filteredLedgers.length > 0 && (
           <Box sx={{ mt: 2 }}>
             <Typography variant="h6" gutterBottom>
               Search Results:
             </Typography>
             <Table>
               <TableHead>
                 <TableRow>
                   <TableCell>First Name</TableCell>
                   <TableCell>Last Name</TableCell>
                   <TableCell>occupation</TableCell>
                   <TableCell>caste</TableCell>
                 </TableRow>
               </TableHead>
               <TableBody>
                 {filteredLedgers.map((ledger) => (
                   <TableRow
                     key={ledger._id}
                     onClick={() => handleLedgerClick(ledger)}
                     hover
                     sx={{ cursor: 'pointer' }}
                   >
                     <TableCell>{ledger.firstName}</TableCell>
                     <TableCell>{ledger.lastName}</TableCell>
                     <TableCell>{ledger.occupation}</TableCell>
                     <TableCell>{ledger.caste}</TableCell>
                   </TableRow>
                 ))}
               </TableBody>
             </Table>
           </Box>
         )}

         {/* Ledger Pledges */}
         {selectedLedger && showLedgerPledges && (
           <Box sx={{ mt: 2 }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
               <Typography variant="h6">
                 Pledges for {selectedLedger.firstName} {selectedLedger.lastName}:
               </Typography>
               <Box>
                 <Button
                   variant="outlined"
                   size="small"
                   onClick={() => fetchLedgerPledges(selectedLedger.code)}
                   sx={{ mr: 1 }}
                 >
                   Refresh
                 </Button>
                 <Button
                   variant="outlined"
                   size="small"
                   onClick={() => setShowLedgerPledges(false)}
                 >
                   Close
                 </Button>
               </Box>
             </Box>
             {loadingLedgerPledges ? (
               <Box display="flex" justifyContent="center" mt={2}>
                 <CircularProgress />
               </Box>
             ) : ledgerPledges.length > 0 ? (
               <Table>
                 <TableHead>
                   <TableRow>
                     <TableCell>Ticket No</TableCell>
                     <TableCell>Principal Amount</TableCell>
                     <TableCell>Date</TableCell>
                     <TableCell>Status</TableCell>
                     <TableCell>Actions</TableCell>
                   </TableRow>
                 </TableHead>
                 <TableBody>
                   {ledgerPledges.map((pledge) => (
                     <TableRow
                       key={pledge._id}
                       hover
                       sx={{ cursor: 'pointer' }}
                     >
                       <TableCell>
                         <Typography variant="body2" fontWeight="bold">
                           {pledge.loanDetails.ticketNo}
                         </Typography>
                       </TableCell>
                                               <TableCell>
                          ₹{Math.round(parseFloat(pledge.loanDetails.principalAmount))}
                        </TableCell>
                       <TableCell>
                         {formatDate(pledge.date)}
                       </TableCell>
                       <TableCell>
                         <Typography 
                           variant="body2" 
                           color={pledge.status === 'RELEASED' ? 'error' : 'success'}
                         >
                           {pledge.status || 'CREATED'}
                         </Typography>
                       </TableCell>
                       <TableCell>
                         <Button
                           variant="contained"
                           size="small"
                           onClick={() => handlePledgeSelect(pledge)}
                         >
                           Select
                         </Button>
                       </TableCell>
                     </TableRow>
                   ))}
                 </TableBody>
               </Table>
             ) : (
               <Typography variant="body1" color="text.secondary">
                 No pledges found for this ledger.
               </Typography>
             )}
           </Box>
         )}

        {pledges.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              type="number"
              label="Months"
              value={selectedInterestType === 'AS_ON_DATE' ? '' : months}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || (Number(value) >= 0 && Number(value) <= 12)) {
                  applyInterestChoice('MONTHS', value);
                }
              }}
              inputProps={{ min: 0, max: 12 }}
              sx={{ width: 120 }}
            />
            <Button
              size="small"
              variant={selectedInterestType === 'AS_ON_DATE' ? 'contained' : 'outlined'}
              onClick={() => applyInterestChoice('AS_ON_DATE')}
            >
              As on date
            </Button>
          </Box>
        )}

        {/* Pledge Table */}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Pledge No</TableCell>
              <TableCell>Principal</TableCell>
              <TableCell>Pending Principal</TableCell>
              <TableCell>Adjusted Principal</TableCell>
              <TableCell> Interest</TableCell>
              <TableCell>Adjusted Interest</TableCell>
              {/* <TableCell>Actions</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {pledges.map((pledge, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setSelectedTicket({
                        ticketNo: pledge.ticketNo,
                        loanDetails: {
                          ticketNo: pledge.ticketNo,
                          principalAmount: pledge.principal,
                          articles: [{
                            rateOfInterest: 0
                          }]
                        },
                      });
                      setShowTicketModal(true);
                    }}
                  >
                    {pledge.ticketNo}
                  </Button>
                </TableCell>
                                 <TableCell>₹{Math.round(pledge.principal)}</TableCell>
                                 <TableCell>₹{Math.round(pledge.pendingPrincipal)}</TableCell>
                <TableCell>
                  <TextField
                    value={pledge.adjustedPrincipal}
                    onChange={(e) => {
                      const newPledges = [...pledges];
                      newPledges[index].adjustedPrincipal = parseFloat(e.target.value) || 0;
                      setPledges(newPledges);
                    }}
                    size="small"
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                </TableCell>
                <TableCell>₹{Math.round(pledge.standardInterest ?? 0)}</TableCell>
                <TableCell>
                  <TextField
                    value={pledge.adjustedInterest}
                    onChange={(e) => {
                      const newPledges = [...pledges];
                      newPledges[index].adjustedInterest = parseFloat(e.target.value) || 0;
                      setPledges(newPledges);
                    }}
                    ref={adjustedInterestRef}
                    size="small"
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                </TableCell>
                {/* <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setSelectedTicket({
                        ticketNo: pledge.ticketNo,
                        loanDetails: {
                          ticketNo: pledge.ticketNo,
                          principalAmount: pledge.principal,
                          articles: [{
                            rateOfInterest: 0
                          }]
                        },
                      });
                      setShowInterestTypeModal(true);
                    }}
                  >
                    Calculate Interest
                  </Button>
                </TableCell> */}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => setShowTicketModal(true)}
          >
            Add Pledge
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveReceipt}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Receipt'}
          </Button>
        </Box>

        {/* Pledge Receipts */}
        {selectedTicket && showPledgeReceipts && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Receipts for Ticket No: {selectedTicket.ticketNo || selectedTicket.loanDetails?.ticketNo}
              </Typography>
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fetchPledgeReceipts(selectedTicket.ticketNo || selectedTicket.loanDetails?.ticketNo)}
                  sx={{ mr: 1 }}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowPledgeReceipts(false)}
                >
                  Close
                </Button>
              </Box>
            </Box>
            {loadingPledgeReceipts ? (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress />
              </Box>
            ) : pledgeReceipts.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Receipt No</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Interest</TableCell>
                    <TableCell>Months</TableCell>
                    <TableCell>Transaction Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pledgeReceipts.map((receipt) => (
                    <TableRow
                      key={receipt._id}
                      hover
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {receipt.receiptNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatDate(receipt.date)}
                      </TableCell>
                      <TableCell>
                        ₹{Math.round(receipt.totalAmount || 0)}
                      </TableCell>
                      <TableCell>
                        ₹{Math.round(receipt.interestDetails?.calculatedInterest || receipt.adjustments?.primaryInterest || 0)}
                      </TableCell>
                      <TableCell>
                        {receipt.interestDetails?.months || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {receipt.transactionType || 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No receipts found for this pledge.
              </Typography>
            )}
          </Box>
        )}
      </Paper>
      </GirviPageShell>

      {/* Ledger Details Dialog */}
      <Dialog open={showLedgerDetails} onClose={handleLedgerDetailsClose} maxWidth="md" fullWidth>
        <DialogTitle>Ledger Details</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={28} />
            </Box>
          ) : selectedLedgerForDetails ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
              {[
                ['Name', `${selectedLedgerForDetails.firstName || ''} ${selectedLedgerForDetails.lastName || ''}`.trim()],
                ['Type', selectedLedgerForDetails.type],
                ['Code', selectedLedgerForDetails.code],
                ['Mobile', selectedLedgerForDetails.mobileNo],
                ['Occupation', selectedLedgerForDetails.occupation],
                ['Caste', selectedLedgerForDetails.caste],
                ['Address', selectedLedgerForDetails.address],
                ['At / Post', selectedLedgerForDetails.atPost],
                ['Village', selectedLedgerForDetails.village],
                ['Taluka', selectedLedgerForDetails.taluka],
                ['District', selectedLedgerForDetails.district],
                ['Aadhar', selectedLedgerForDetails.aadharCardNo],
                ['PAN', selectedLedgerForDetails.panCardNo],
              ].map(([label, value]) => (
                <Box key={label} sx={{ px: 1, py: 0.6, borderRadius: 1, bgcolor: '#fff', border: '1px solid #e2e8f0', gridColumn: label === 'Name' || label === 'Address' ? '1 / -1' : 'auto' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', lineHeight: 1.2 }}>{label}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>{value || '—'}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" align="center">
              Select a ledger to view details
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLedgerDetailsClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleLedgerDetailsConfirm} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Modal */}
      <Dialog open={showTicketModal} onClose={() => setShowTicketModal(false)}>
        <DialogTitle>Ticket Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Ticket No"
              value={selectedTicket?.ticketNo || ''}
              onChange={(e) => {
                setSelectedTicket(prev => ({
                  ...prev,
                  ticketNo: e.target.value
                }));
              }}
              fullWidth
              sx={{ mb: 2 }}
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
            {loadingPledge ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Box>
            ) : pledgeDetails ? (
              <Box>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Principal Amount: ₹{pledgeDetails.principalAmount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Created At: {new Date(pledgeDetails.createdAt).toLocaleString()}
                </Typography>
                
                {selectedTicket?.loanDetails?.articles && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Interest Rates
                    </Typography>
                    <Table>
                      <TableBody>
                        {selectedTicket.loanDetails.articles.map((article, index) => (
                          <TableRow key={index}>
                            <TableCell>Article {index + 1}</TableCell>
                            <TableCell>
                              Rate of Interest: {article.rateOfInterest}%
                            </TableCell>
                          </TableRow>
                        ))}
                        
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={() => fetchPledgeDetails(selectedTicket?.ticketNo)}
                fullWidth
                sx={{ mt: 2 }}
              >
                Fetch Pledge Details
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTicketModal(false)}>Cancel</Button>
          <Button onClick={handleTicketSelect} disabled={!pledgeDetails} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Interest Type Selection Modal */}
      <Dialog open={showInterestTypeModal} onClose={() => {
        setShowInterestTypeModal(false);
      }}>
        <DialogTitle>Select Interest Type</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {INTEREST_TYPE_OPTIONS.map((opt, idx) => (
              <Button
                key={opt.value}
                fullWidth
                variant={selectedInterestType === opt.value ? 'contained' : 'outlined'}
                onClick={() => handleInterestTypeSelect(opt.value)}
                sx={{ mb: idx < INTEREST_TYPE_OPTIONS.length - 1 ? 2 : 0 }}
              >
                {opt.label}
              </Button>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Month Input Modal */}
      <Dialog open={showInterestModal} onClose={() => {
        setShowInterestModal(false);
      }}>
        <DialogTitle>Enter Number of Months</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Number of Months"
            value={months}
            onChange={handleMonthInput}
            inputProps={{ 
              min: 0, 
              max: 12,
              style: { textTransform: 'uppercase' }
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInterestModal(false)}>Cancel</Button>
          <Button onClick={handleMonthConfirm} color="primary">
            Calculate
          </Button>
        </DialogActions>
      </Dialog>


      {/* Fingerprint Matches Dialog */}
      <Dialog 
        open={showFingerprintMatches} 
        onClose={() => setShowFingerprintMatches(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Fingerprint Matches ({fingerprintMatches.length})
        </DialogTitle>
        <DialogContent>
          {fingerprintScanning ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={2}>
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Scanning fingerprint... Please place your finger on the scanner.
              </Typography>
            </Box>
          ) : fingerprintMatches.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket No</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Principal Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Match Score</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fingerprintMatches.map((pledge) => (
                  <TableRow
                    key={pledge._id}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: (pledge.fingerprintMatchScore || 0) >= 90 
                        ? 'rgba(76, 175, 80, 0.1)' 
                        : (pledge.fingerprintMatchScore || 0) >= 70 
                        ? 'rgba(255, 193, 7, 0.1)' 
                        : 'transparent'
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {pledge.loanDetails.ticketNo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {pledge.customer.firstName} {pledge.customer.lastName || ''}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        Code: {pledge.customer.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      ₹{Math.round(parseFloat(pledge.loanDetails.principalAmount))}
                    </TableCell>
                    <TableCell>
                      {formatDate(pledge.date)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${pledge.fingerprintMatchScore || 0}%`}
                        color={
                          (pledge.fingerprintMatchScore || 0) >= 90 ? 'success' :
                          (pledge.fingerprintMatchScore || 0) >= 70 ? 'warning' : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleFingerprintPledgeSelect(pledge)}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 3 }}>
              No matching pledges found.
            </Typography>
          )}
          
          {fingerprintMatches.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info">
                <strong>Match Score Guide:</strong>
                <br />
                • 90-100%: Excellent match (green)
                <br />
                • 70-89%: Good match (yellow)
                <br />
                • Below 70%: Lower confidence - verify manually
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFingerprintMatches(false)}>Close</Button>
          <Button 
            onClick={handleFingerprintScan}
            disabled={fingerprintScanning}
            startIcon={<FingerprintIcon />}
          >
            Scan Again
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default CreateReceipt;